import { useState, useEffect } from 'react';
import { X } from 'lucide-react';

interface CustomTipModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (tip: string, mode: 'percentage' | 'amount') => void;
  initialTip?: string;
  initialMode?: 'percentage' | 'amount';
  formatCurrency: (amount: number) => string;
  calculateSubtotal: () => number;
  t: {
    enterTip: string;
    percentage: string;
    amount: string;
    enterTipPercentage: string;
    enterTipAmount: string;
    estimatedTip: string;
    cancel: string;
    apply: string;
  };
}

/**
 * 커스텀 팁 입력 모달 컴포넌트
 *
 * 사용자가 퍼센트 또는 금액 방식으로 팁을 입력할 수 있습니다.
 * 내부 상태(tempCustomTip, tempCustomTipMode)를 관리하며,
 * 모달 닫을 때 자동으로 상태를 리셋합니다.
 */
export function CustomTipModal({
  isOpen,
  onClose,
  onApply,
  initialTip = '',
  initialMode = 'percentage',
  formatCurrency,
  calculateSubtotal,
  t
}: CustomTipModalProps) {
  // 내부 상태 관리
  const [tempCustomTip, setTempCustomTip] = useState<string>(initialTip);
  const [tempCustomTipMode, setTempCustomTipMode] = useState<'percentage' | 'amount'>(initialMode);

  // 모달이 열릴 때 초기값으로 리셋
  useEffect(() => {
    if (isOpen) {
      setTempCustomTip(initialTip);
      setTempCustomTipMode(initialMode);
    }
  }, [isOpen, initialTip, initialMode]);

  // 모달 닫기 핸들러 (상태 리셋 포함)
  const handleClose = () => {
    setTempCustomTip('');
    setTempCustomTipMode('percentage');
    onClose();
  };

  // 입력값 정규화 (검증 + 클램핑 + 소수점 2자리 라운딩)
  const normalizeTip = (value: string, mode: 'percentage' | 'amount'): number => {
    const parsed = parseFloat(value);
    if (!Number.isFinite(parsed) || parsed < 0) return 0;
    if (mode === 'percentage') return Math.min(parsed, 100);
    return Math.round(parsed * 100) / 100;
  };

  // 입력값이 유효한 양수인지 확인
  const isValidPositiveTip = (): boolean => {
    const trimmed = tempCustomTip.trim();
    if (!trimmed) return false;
    const parsed = parseFloat(trimmed);
    return Number.isFinite(parsed) && parsed > 0;
  };

  // 적용 핸들러 (빈 입력 또는 무효한 값 = 커스텀 팁 해제)
  const handleApply = () => {
    if (isValidPositiveTip()) {
      const normalized = normalizeTip(tempCustomTip, tempCustomTipMode);
      onApply(String(normalized), tempCustomTipMode);
    } else {
      onApply('', tempCustomTipMode);
    }
    setTempCustomTip('');
    setTempCustomTipMode('percentage');
  };

  // 예상 팁 금액 계산 (usePaymentCalculations와 동일한 소수점 2자리 라운딩)
  // normalizeTip을 적용하여 미리보기와 적용 결과가 일치하도록 함
  const calculateEstimatedTip = (): number => {
    const subtotal = calculateSubtotal();
    const tipValue = normalizeTip(tempCustomTip, tempCustomTipMode);

    if (tempCustomTipMode === 'percentage') {
      return Math.round(subtotal * (tipValue / 100) * 100) / 100;
    }
    return Math.round(tipValue * 100) / 100;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-background rounded-2xl shadow-2xl w-full max-w-md animate-slideUp">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-border">
          <h3 className="text-lg sm:text-xl font-bold text-foreground">
            {t.enterTip}
          </h3>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-muted rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-4 sm:p-5 space-y-4">
          {/* Toggle Mode Button */}
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => { if (tempCustomTipMode !== 'percentage') { setTempCustomTipMode('percentage'); setTempCustomTip(''); } }}
              className={`h-[50px] text-sm font-medium transition-all duration-200 text-center rounded-[5px] border-2 active:scale-95 ${
                tempCustomTipMode === 'percentage'
                  ? 'bg-primary text-white border-primary shadow-sm'
                  : 'bg-background text-muted-foreground border-border hover:border-primary hover:bg-primary/5'
              }`}
            >
              {t.percentage}
            </button>
            <button
              onClick={() => { if (tempCustomTipMode !== 'amount') { setTempCustomTipMode('amount'); setTempCustomTip(''); } }}
              className={`h-[50px] text-sm font-medium transition-all duration-200 text-center rounded-[5px] border-2 active:scale-95 ${
                tempCustomTipMode === 'amount'
                  ? 'bg-primary text-white border-primary shadow-sm'
                  : 'bg-background text-muted-foreground border-border hover:border-primary hover:bg-primary/5'
              }`}
            >
              {t.amount}
            </button>
          </div>

          {/* Input Field */}
          <div className="space-y-3">
            <label className="block text-xs sm:text-sm text-muted-foreground text-center">
              {tempCustomTipMode === 'percentage' ? t.enterTipPercentage : t.enterTipAmount}
            </label>
            <div className="relative">
              <input
                type="number"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="0"
                value={tempCustomTip}
                onChange={(e) => setTempCustomTip(e.target.value)}
                className="w-full h-[80px] px-6 text-center text-3xl font-bold transition-all duration-200 border-2 rounded-[5px] bg-background border-border text-foreground placeholder:text-muted-foreground/30 focus:border-primary focus:ring-0 outline-none"
                autoFocus
              />
              {tempCustomTipMode === 'percentage' && tempCustomTip && (
                <span className="absolute right-6 top-1/2 -translate-y-1/2 text-3xl font-bold text-primary">
                  %
                </span>
              )}
            </div>

            {/* Preview */}
            {tempCustomTip && (
              <div className="p-4 bg-muted rounded-[5px] border border-border">
                <p className="text-xs text-muted-foreground text-center mb-2">
                  {t.estimatedTip}
                </p>
                <p className="text-2xl font-bold text-primary text-center">
                  {formatCurrency(calculateEstimatedTip())}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex gap-2 p-4 border-t border-border">
          <button
            onClick={handleClose}
            className="flex-1 h-[50px] text-sm font-medium rounded-[5px] border-2 border-border text-muted-foreground bg-background hover:bg-muted hover:border-background-secondary transition-all duration-200 active:scale-95 flex items-center justify-center"
          >
            {t.cancel}
          </button>
          <button
            onClick={handleApply}
            disabled={!tempCustomTip.trim()}
            className="flex-1 h-[50px] text-sm font-medium rounded-[5px] bg-primary text-white hover:bg-primary-hover disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed transition-all duration-200 active:scale-95 disabled:active:scale-100 shadow-sm disabled:shadow-none flex items-center justify-center"
          >
            {t.apply}
          </button>
        </div>
      </div>
    </div>
  );
}
